import type { CncSpeciesAssignment, TimberRfqDrynessSpec, VendorWorkType } from '../types';
import {
  TIMBER_SUPPLIER_SURFACE_SPEC,
  timberRfqDrynessSpecLabel,
  timberSupplierDimensionsSpecForLists,
  timberSupplierDimensionsSpecLabel,
  formatTimberSpeciesDrynessForEmailDetailHtml,
  type TimberSpeciesDrynessEntry,
} from './supplierTimberExport';
import { cncMaterialFlowLabel } from './cncRfqAssignments';
import { formatSupplierShipToHtml } from './supplierShipTo';

const PORTAL_URL = 'https://clients.lctimberframes.com/vendor-portal';
const DEFAULT_COMPANY = 'LC Timber Frames';
export const RFQ_EMAIL_SIGNATURE_NAME = "Leiper's Creek Timber Frames";
export const RFQ_PUBLISH_LOGO_URL =
  'https://clients.lctimberframes.com/assets/logo/LCTF%20Profile.png';

export type RfqPublishEmailContext = {
  recipientFirstName?: string;
  /** Supplier org name shown in the email header. */
  vendorOrgName?: string;
  /** Staff member publishing the RFQ — shown in the email signature. */
  senderName?: string;
  dealTitle: string;
  projectNumber?: string;
  projectName?: string;
  requestedSpecies?: string[];
  workType: VendorWorkType;
  customMessage?: string;
  /** Timber RFQ dryness requirement (email specs block). */
  timberDrynessSpec?: TimberRfqDrynessSpec | null;
  /** Per-species dryness when species differ within one supplier request. */
  speciesDryness?: TimberSpeciesDrynessEntry[];
  /** CNC quote requests only — the species this shop is asked to cut, with material flow. */
  cncAssignments?: CncSpeciesAssignment[];
  /** CNC quote requests only — structure names this shop quotes; absent = all structures. */
  cncStructures?: string[];
  /** Timber RFQ only — structure names this supplier quotes; absent = all structures. */
  timberStructures?: string[];
  /**
   * Timber RFQ only — species to price per structure when they differ by structure
   * (structure name → species). Structures not listed take every requested species.
   */
  timberStructureSpecies?: Array<{ name: string; species: string[] }>;
  /**
   * Scope-change resend — renders the "same file, updated scope" banner and
   * switches heading/subject copy. The released file itself is unchanged.
   */
  scopeChange?: {
    /** Version/file the supplier already received, e.g. "v3 — Barn.3d". */
    fileLabel?: string;
    /** Structure names newly added to this supplier's scope. */
    added?: string[];
    /** Structure names removed from this supplier's scope. */
    removed?: string[];
    /** Per-structure species changes, e.g. "Sale Barn — Glulam only (was Glulam, Pine)". */
    speciesChanges?: string[];
  };
  /** Resolved delivery address for this supplier package. */
  shipToAddress?: import('../types').SupplierShipToAddress;
  /** Files → RFQs request round (one email covering every structure this supplier quotes). */
  requestRound?: RfqRequestRoundEmail;
  /**
   * Reference release — a refined file shared after the quote was approved. The
   * supplier cuts from it; no quote is requested. Switches heading/subject/body.
   */
  referenceRelease?: boolean;
  /** Supplier-facing release name, e.g. "3D Model v3" — shown in reference-release copy. */
  releaseLabel?: string;
  /** Single-list quote. Request rounds use each line's flag instead. */
  trueDimensions?: boolean;
  /** Per-list flags when a package email covers more than one timber list. */
  dimensionLists?: Array<{ label?: string; trueDimensions?: boolean }>;
  companyName?: string;
  portalUrl?: string;
  logoUrl?: string;
};

/** One structure's timber list inside a request round, as this supplier sees it. */
export type RfqRequestRoundLineEmail = {
  /** Structure names on this list; empty = whole project. */
  structures: string[];
  /** File label suppliers see, e.g. "Sale Barn — Timber List - Concept Only". */
  displayLabel: string;
  versionLabel?: string;
  versionIndex?: number;
  /** Species on this list when every structure on it is priced the same way. */
  species?: string[];
  /** Species to price on each structure in this list. */
  structureSpecies?: Array<{ name: string; species: string[] }>;
  /** Set when this list replaces one the supplier already quoted. */
  supersedes?: { versionLabel?: string; versionIndex?: number; quoteTotal?: number };
  /** Cross-section inches stay as written in the CSV. */
  trueDimensions?: boolean;
};

/** Request-round envelope — mirrors `functions/src/rfqPublishEmail.ts`. */
export type RfqRequestRoundEmail = {
  label: string;
  /** "Concept" | "Production". */
  stageLabel?: string;
  /** Pre-formatted due date, e.g. "Fri, Oct 24, 2026". */
  dueDateText?: string;
  lines: RfqRequestRoundLineEmail[];
};

/** "Timber quotes" (the default label) carries no information worth repeating in subject/heading. */
export function isGenericRfqRequestLabel(label: string | undefined): boolean {
  const l = (label || '').trim().toLowerCase();
  return !l || l === 'timber quotes' || l === 'timber quote request' || l === 'quote request';
}

export function rfqRequestRoundEmailSubject(
  projectNumber: string | undefined,
  projectName: string | undefined,
  label: string | undefined,
): string {
  const title = rfqProjectEmailLabel(projectNumber, projectName);
  const lbl = (label || '').trim();
  return lbl && !isGenericRfqRequestLabel(lbl) ? `Timber quote request — ${lbl}: ${title}` : `Timber quote request: ${title}`;
}

/** "Pet Porch (White Oak, Doug Fir), Playroom Porch (EWP)". */
function requestRoundStructuresPhrase(round: RfqRequestRoundEmail): string {
  const parts: string[] = [];
  for (const line of round.lines) {
    const rows = line.structureSpecies?.length
      ? line.structureSpecies
      : line.structures.map((name) => ({ name, species: line.species ?? [] }));
    if (!rows.length) {
      const species = (line.species ?? []).map((s) => s.trim()).filter(Boolean);
      parts.push(species.length ? `Whole project (${species.join(', ')})` : 'Whole project');
      continue;
    }
    for (const row of rows) {
      const name = (row.name || '').trim();
      if (!name) continue;
      const species = (row.species || []).map((s) => s.trim()).filter(Boolean);
      parts.push(species.length ? `${name} (${species.join(', ')})` : name);
    }
  }
  return parts.join(', ');
}

function requestRoundGreetingHtml(firstNameHtml: string, round: RfqRequestRoundEmail): string {
  const oneList = round.lines.length === 1;
  const structures = requestRoundStructuresPhrase(round);
  const which = structures
    ? `, which ${oneList ? 'includes' : 'include'} the following structures: ${escapeHtml(structures)}`
    : '';
  return `<p style="color: #4F423A; line-height: 1.65; margin: 0 0 16px; font-size: 14px;">
        Hi ${firstNameHtml},<br/><br/>
        Please price out the attached timber ${oneList ? 'list' : 'lists'}${which}.
      </p>`;
}

function speciesListText(species: string[] | undefined): string {
  const list = (species || []).map((s) => s.trim()).filter(Boolean);
  if (!list.length) return 'see project details below';
  return list.join(', ');
}

function speciesListHtml(species: string[] | undefined): string {
  const text = speciesListText(species);
  if (text === 'see project details below') return escapeHtml(text);
  return `<strong>${escapeHtml(text)}</strong>`;
}

/** Bold species names (and full list phrase) in already-escaped HTML. */
export function boldSpeciesInHtml(html: string, species: string[] | undefined): string {
  const list = (species || []).map((s) => s.trim()).filter(Boolean);
  if (!list.length) return html;

  let out = html;
  const phrases = [list.join(', '), ...list].sort((a, b) => b.length - a.length);
  for (const phrase of phrases) {
    const escapedPhrase = escapeHtml(phrase);
    if (!escapedPhrase || out.includes(`<strong>${escapedPhrase}</strong>`)) continue;
    out = out.split(escapedPhrase).join(`<strong>${escapedPhrase}</strong>`);
  }
  return out;
}

function projectDisplayLabel(projectNumber?: string, projectDescriptor?: string): string {
  return rfqProjectEmailLabel(projectNumber, projectDescriptor);
}

/** Supplier-facing project line: `{projectNumber} {descriptor}` — never includes client name. */
export function rfqProjectEmailLabel(
  projectNumber?: string,
  projectDescriptor?: string,
): string {
  const num = (projectNumber || '').trim();
  const numDisplay = num ? (num.startsWith('#') ? num : `#${num}`) : '';
  const desc = (projectDescriptor || '').trim();
  if (numDisplay && desc) return `${numDisplay} ${desc}`;
  return numDisplay || desc || 'Project';
}

/** Timber RFQ PDF / package title — prefer live deal naming, not stale rfqProject.dealTitle. */
export function resolveRfqSupplierProjectLabel(input: {
  projectNumber?: string;
  descriptor?: string;
  dealTitleFallback?: string;
  dealIdFallback?: string;
}): string {
  const label = rfqProjectEmailLabel(input.projectNumber, input.descriptor);
  if (label !== 'Project') return label;
  const legacy = (input.dealTitleFallback || '').trim();
  if (legacy) return legacy;
  return (input.dealIdFallback || 'Project').trim();
}

function parseLegacyDescriptorFromTitle(title: string | undefined): string {
  const t = (title || '').trim();
  if (!t) return '';
  const dashIdx = t.indexOf(' - ');
  if (dashIdx === -1) return '';
  return t.slice(dashIdx + 3).trim();
}

/** Resolve descriptor for RFQ emails — explicit field first, then legacy title suffix only. */
export function rfqProjectEmailDescriptor(input: {
  descriptor?: string;
  dealTitle?: string;
}): string | undefined {
  const explicit = (input.descriptor || '').trim();
  if (explicit) return explicit;
  const fromTitle = parseLegacyDescriptorFromTitle(input.dealTitle);
  return fromTitle || undefined;
}

export function defaultRfqPublishEmailMessage(ctx: {
  recipientFirstName?: string;
  requestedSpecies?: string[];
  workType: VendorWorkType;
}): string {
  const first = (ctx.recipientFirstName || '').trim() || 'there';
  const species = speciesListText(ctx.requestedSpecies);

  if (ctx.workType === 'cnc_quote') {
    return (
      `Hi ${first},\n\n` +
      `A new CNC quote request is available for bidding. Please download the released model files from the vendor portal and submit your quote when you are ready.\n\n` +
      `Both the model files and more detailed project information are available in the project portal.`
    );
  }

  return (
    `Hi ${first},\n\n` +
    `A new timber RFQ package is available for bidding. Please provide pricing for the following species: ${species}. ` +
    `Please find the CSV attached to this email. Both the CSV and more detailed project information are available in the project portal.`
  );
}

/** Default editable draft for a reference release (refined file after an award — no requote). */
export function defaultRfqReferenceReleaseEmailMessage(ctx: {
  recipientFirstName?: string;
  workType: VendorWorkType;
  /** Supplier-facing release name, e.g. "3D Model v3". */
  releaseLabel?: string;
  /** Structure names this release covers; empty = all structures. */
  structureNames?: string[];
}): string {
  const first = (ctx.recipientFirstName || '').trim() || 'there';
  const label = (ctx.releaseLabel || '').trim();
  const labelRef = label ? ` (${label})` : '';
  const structures = (ctx.structureNames || []).map(s => s.trim()).filter(Boolean);
  const coversRef = structures.length ? ` It covers ${structures.join(', ')}.` : '';
  const thing = ctx.workType === 'cnc_quote' ? 'model' : 'timber list';
  const where =
    ctx.workType === 'cnc_quote'
      ? 'Download the updated model files from the vendor portal.'
      : 'The updated list is attached and available in the vendor portal.';
  return (
    `Hi ${first},\n\n` +
    `We've refined the ${thing} for the package you already quoted and we approved${labelRef}.${coversRef}\n\n` +
    `This is a reference release — please do not requote. Use this version to cut the package; your approved pricing stands. ${where}`
  );
}

/** Default editable draft for a scope-change resend ("same file, updated scope"). */
export function defaultRfqScopeChangeEmailMessage(ctx: {
  recipientFirstName?: string;
  workType: VendorWorkType;
  /** Version/file the supplier already received, e.g. "v3 — Barn.3d". */
  fileLabel?: string;
}): string {
  const first = (ctx.recipientFirstName || '').trim() || 'there';
  const fileLabel = (ctx.fileLabel || '').trim();
  const fileRef = fileLabel ? ` (${fileLabel})` : '';

  if (ctx.workType === 'cnc_quote') {
    return (
      `Hi ${first},\n\n` +
      `We've updated which structures we need you to quote on this project. This is not a new file — the model files you already received${fileRef} are unchanged.\n\n` +
      `The updated structure list is below and in the vendor portal.`
    );
  }

  return (
    `Hi ${first},\n\n` +
    `We've updated which structures we need you to quote on this project. This is not a new timber list version${fileRef} — only your requested scope changed. ` +
    `The attached CSV reflects your updated scope, and the full details are in the vendor portal.`
  );
}

/** Apply per-recipient greeting and species to a shared draft before preview/send. */
export function personalizeRfqPublishEmailMessage(
  customMessage: string,
  ctx: {
    recipientFirstName?: string;
    requestedSpecies?: string[];
    workType: VendorWorkType;
  },
): string {
  const trimmed = customMessage.trim();
  if (!trimmed) return trimmed;

  const first = (ctx.recipientFirstName || '').trim() || 'there';
  let msg = trimmed.replace(/^Hi\s+[^,\n]+,/i, `Hi ${first},`);

  if (ctx.workType === 'cnc_quote') return msg;

  const species = speciesListText(ctx.requestedSpecies);
  msg = msg.replace(
    /Please provide pricing for the following species: [^\n.]+\./,
    `Please provide pricing for the following species: ${species}.`,
  );

  return msg;
}

export function rfqPublishEmailSubject(
  projectNumber: string | undefined,
  projectDescriptor: string | undefined,
  workType: VendorWorkType,
  scopeChange = false,
  referenceRelease = false,
): string {
  const title = rfqProjectEmailLabel(projectNumber, projectDescriptor);
  if (referenceRelease) {
    return workType === 'cnc_quote'
      ? `Updated model for production (no requote) — ${title}`
      : `Updated timber list for production (no requote) — ${title}`;
  }
  if (scopeChange) {
    return workType === 'cnc_quote'
      ? `Updated scope (same file) — CNC quote request: ${title}`
      : `Updated scope (same file) — timber RFQ: ${title}`;
  }
  return workType === 'cnc_quote'
    ? `New CNC quote request: ${title}`
    : `New timber RFQ package: ${title}`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function messageHtml(customMessage: string | undefined, fallbackHtml: string): string {
  const trimmed = (customMessage || '').trim();
  if (!trimmed) return fallbackHtml;
  const inner = trimmed
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br/>');
  return `<p style="color: #4F423A; line-height: 1.65; margin: 0 0 16px; font-size: 14px;">${inner}</p>`;
}

function buildSenderSignature(ctx: RfqPublishEmailContext): string {
  const sender = (ctx.senderName || '').trim();
  const organization = escapeHtml(RFQ_EMAIL_SIGNATURE_NAME);
  if (!sender) {
    return `
    <p style="color: #4F423A; margin: 28px 0 0; font-size: 14px; line-height: 1.6;">
      Best regards,<br/>
      <strong>${organization}</strong>
    </p>
  `.trim();
  }
  return `
    <p style="color: #4F423A; margin: 28px 0 0; font-size: 14px; line-height: 1.6;">
      Best regards,<br/>
      <strong>${escapeHtml(sender)}</strong><br/>
      <span style="color: #6B6960;">${organization}</span>
    </p>
  `.trim();
}

function timberDimensionsSpecText(ctx: RfqPublishEmailContext): string {
  const roundLines = ctx.requestRound?.lines ?? [];
  if (roundLines.length) {
    return timberSupplierDimensionsSpecForLists(
      roundLines.map((line) => ({ trueDimensions: line.trueDimensions, label: line.displayLabel })),
    );
  }
  if (ctx.dimensionLists?.length) return timberSupplierDimensionsSpecForLists(ctx.dimensionLists);
  return timberSupplierDimensionsSpecLabel(ctx.trueDimensions);
}

function buildTimberSpecsSectionHtml(ctx: RfqPublishEmailContext): string {
  const drynessHtml = ctx.speciesDryness?.length
    ? formatTimberSpeciesDrynessForEmailDetailHtml(ctx.speciesDryness)
    : escapeHtml(timberRfqDrynessSpecLabel(ctx.timberDrynessSpec));
  return `
      <p style="margin: 16px 0 8px; font-size: 13px; color: #6B6960; text-transform: uppercase; letter-spacing: 0.04em;">Specifications</p>
      <ul style="margin: 0; padding-left: 18px; color: #4F423A; font-size: 14px; line-height: 1.55;">
        <li><strong>Dryness:</strong> ${drynessHtml}</li>
        <li><strong>Dimensions:</strong> ${escapeHtml(timberDimensionsSpecText(ctx))}</li>
        <li><strong>Surface finish:</strong> ${escapeHtml(TIMBER_SUPPLIER_SURFACE_SPEC)}</li>
      </ul>
  `.trim();
}

function buildCncSpeciesSectionHtml(assignments: CncSpeciesAssignment[]): string {
  const speciesItems = assignments
    .map((a) => {
      const bdft = a.boardFeet && a.boardFeet > 0 ? ` — ~${a.boardFeet.toLocaleString()} bdft` : '';
      return `<li><strong>${escapeHtml(a.species.trim())}</strong>${escapeHtml(bdft)}</li>`;
    })
    .join('');
  const materialItems = assignments
    .map((a) => {
      const note = (a.note || '').trim();
      return `<li>${escapeHtml(cncMaterialFlowLabel(a, 'supplier'))}${note ? ` — ${escapeHtml(note)}` : ''}</li>`;
    })
    .join('');
  return `
      <p style="margin: 0 0 8px; font-size: 13px; color: #6B6960; text-transform: uppercase; letter-spacing: 0.04em;">Species to cut</p>
      <ul style="margin: 0 0 12px; padding-left: 18px; color: #4F423A; font-size: 14px; line-height: 1.55;">${speciesItems}</ul>
      <p style="margin: 0 0 8px; font-size: 13px; color: #6B6960; text-transform: uppercase; letter-spacing: 0.04em;">Material</p>
      <ul style="margin: 0; padding-left: 18px; color: #4F423A; font-size: 14px; line-height: 1.55;">${materialItems}</ul>
  `.trim();
}

function buildProjectDetailsBlock(ctx: RfqPublishEmailContext): string {
  const projectLabel = escapeHtml(
    projectDisplayLabel(ctx.projectNumber, rfqProjectEmailDescriptor({
      descriptor: ctx.projectName,
      dealTitle: ctx.dealTitle,
    })),
  );
  const isCnc = ctx.workType === 'cnc_quote';
  const hasSpecies = (ctx.requestedSpecies || []).some(s => s.trim());
  const cncAssignments = (ctx.cncAssignments || []).filter(a => a.species?.trim());
  const structureNames = (isCnc ? ctx.cncStructures : ctx.timberStructures) ?? [];
  const structures = structureNames.map(s => s.trim()).filter(Boolean);
  const perStructure = !isCnc && !ctx.requestRound
    ? (ctx.timberStructureSpecies || []).filter(row => row.name?.trim())
    : [];
  const speciesOfStructure = (name: string): string[] | undefined => {
    const row = perStructure.find(r => r.name.trim().toLowerCase() === name.trim().toLowerCase());
    return row ? row.species : undefined;
  };
  // Request rounds list species beside each structure above; repeating the union here reads as a contradiction.
  const roundListsSpecies = !!ctx.requestRound?.lines?.some(l => (l.species ?? []).some(s => s.trim()));
  const structuresBlock = ctx.requestRound
    ? ''
    : structures.length
    ? perStructure.length
      ? `<p style="margin: 0 0 4px; font-size: 14px; color: #4F423A; line-height: 1.5;"><strong>Structures to quote</strong> — species to price are listed with each one:</p>
      <ul style="margin: 0 0 12px; padding-left: 20px; font-size: 14px; color: #4F423A; line-height: 1.6;">
        ${structures.map(name => {
          const sp = speciesOfStructure(name) ?? ctx.requestedSpecies ?? [];
          const spText = sp.length ? speciesListHtml(sp) : '<em>no species</em>';
          return `<li>${escapeHtml(name)} — ${spText}</li>`;
        }).join('')}
      </ul>`
      : `<p style="margin: 0 0 12px; font-size: 14px; color: #4F423A; line-height: 1.5;">
        <strong>Structures to quote:</strong> ${escapeHtml(structures.join(', '))}
      </p>`
    : '';
  const speciesBlock = isCnc
    ? cncAssignments.length
      ? buildCncSpeciesSectionHtml(cncAssignments)
      : ''
    : hasSpecies && !perStructure.length && !roundListsSpecies
      ? `<p style="margin: 0; font-size: 14px; color: #4F423A; line-height: 1.5;">
        Requested species: ${speciesListHtml(ctx.requestedSpecies)}
      </p>`
      : '';
  const shipToBlock = ctx.shipToAddress?.line1?.trim()
    ? `<p style="margin: 12px 0 0; font-size: 14px; color: #4F423A; line-height: 1.5;">
        <strong>Ship to:</strong><br/>${formatSupplierShipToHtml(ctx.shipToAddress)}
      </p>`
    : '';

  return `
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="margin: 28px 0 20px; border-collapse: separate; border-spacing: 0;">
      <tr>
        <td style="background: #F7F5F0; border: 1px solid #E2E0DB; border-radius: 8px; padding: 16px 20px;">
          <p style="margin: 0 0 8px; font-size: 13px; color: #6B6960; text-transform: uppercase; letter-spacing: 0.04em;">Project</p>
          <p style="margin: 0 0 12px; font-size: 16px; font-weight: 650; color: #2C3E2F;">${projectLabel}</p>
          ${structuresBlock}
          ${speciesBlock}
          ${shipToBlock}
          ${isCnc ? '' : buildTimberSpecsSectionHtml(ctx)}
        </td>
      </tr>
    </table>
  `.trim();
}

/** Amber "same file, updated scope" banner shown on scope-change resends. */
function buildScopeChangeBannerHtml(ctx: RfqPublishEmailContext): string {
  const sc = ctx.scopeChange;
  if (!sc) return '';
  const fileLabel = (sc.fileLabel || '').trim();
  const added = (sc.added || []).map(s => s.trim()).filter(Boolean);
  const removed = (sc.removed || []).map(s => s.trim()).filter(Boolean);
  const changeBits: string[] = [];
  if (added.length) changeBits.push(`<strong>Added:</strong> ${escapeHtml(added.join(', '))}`);
  if (removed.length) changeBits.push(`<strong>Removed:</strong> ${escapeHtml(removed.join(', '))}`);
  const speciesChanges = (sc.speciesChanges || []).map(s => s.trim()).filter(Boolean);
  if (speciesChanges.length) {
    changeBits.push(`<strong>Species by structure:</strong> ${speciesChanges.map(escapeHtml).join('; ')}`);
  }
  const changesHtml = changeBits.length
    ? `<p style="margin: 8px 0 0; font-size: 13px; color: #7C5A12; line-height: 1.5;">${changeBits.join('<br/>')}</p>`
    : '';
  const fileHtml = fileLabel
    ? `The <strong>${escapeHtml(fileLabel)}</strong> package you already received is unchanged`
    : 'The package files you already received are unchanged';
  return `
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="margin: 0 0 16px; border-collapse: separate; border-spacing: 0;">
        <tr>
          <td style="background: #FEF8EC; border: 1px solid #F0DCAE; border-radius: 8px; padding: 12px 16px;">
            <p style="margin: 0; font-size: 13px; color: #7C5A12; line-height: 1.5;">
              <strong>Scope update — not a new file.</strong> ${fileHtml} — only ${speciesChanges.length && !added.length && !removed.length ? 'the species we are asking you to price on each structure have' : speciesChanges.length ? 'the structures and species we are asking you to quote have' : 'the structures we are asking you to quote have'} changed.
            </p>
            ${changesHtml}
          </td>
        </tr>
      </table>
  `.trim();
}

/** Amber "reference release — do not requote" banner. */
function buildReferenceReleaseBannerHtml(ctx: RfqPublishEmailContext): string {
  const isCnc = ctx.workType === 'cnc_quote';
  const structures = ((isCnc ? ctx.cncStructures : ctx.timberStructures) ?? [])
    .map(s => s.trim())
    .filter(Boolean);
  const coversHtml = structures.length
    ? `<p style="margin: 8px 0 0; font-size: 13px; color: #7C5A12; line-height: 1.5;"><strong>Covers:</strong> ${escapeHtml(structures.join(', '))}</p>`
    : `<p style="margin: 8px 0 0; font-size: 13px; color: #7C5A12; line-height: 1.5;"><strong>Covers:</strong> all structures on this package</p>`;
  return `
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="margin: 0 0 16px; border-collapse: separate; border-spacing: 0;">
        <tr>
          <td style="background: #FEF8EC; border: 1px solid #F0DCAE; border-radius: 8px; padding: 12px 16px;">
            <p style="margin: 0; font-size: 13px; color: #7C5A12; line-height: 1.5;">
              <strong>Reference release — not a quote request.</strong> This is the refined ${isCnc ? 'model' : 'timber list'} for the package you already quoted. Build from this version; your approved pricing is unchanged.
            </p>
            ${coversHtml}
          </td>
        </tr>
      </table>
  `.trim();
}

function wrapRfqEmailDocument(title: string, bodyRows: string): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(title)}</title>
</head>
<body style="margin:0;padding:0;background-color:#F7F5F0;-webkit-text-size-adjust:100%;">
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#F7F5F0;">
    <tr>
      <td align="center" style="padding:24px 16px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="560" style="max-width:560px;width:100%;border-collapse:collapse;">
          ${bodyRows}
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`.trim();
}

export function buildRfqPublishEmailHtml(ctx: RfqPublishEmailContext): string {
  const companyNameRaw = ctx.companyName?.trim() || DEFAULT_COMPANY;
  const companyName = escapeHtml(companyNameRaw);
  const portalUrl = ctx.portalUrl?.trim() || PORTAL_URL;
  const logoUrl = ctx.logoUrl?.trim() || RFQ_PUBLISH_LOGO_URL;
  const vendorHeader = escapeHtml((ctx.vendorOrgName || '').trim() || 'Vendor Portal');
  const isCnc = ctx.workType === 'cnc_quote';
  const isScopeChange = !!ctx.scopeChange;
  const isReference = !!ctx.referenceRelease && !ctx.requestRound;
  const first = escapeHtml((ctx.recipientFirstName || '').trim() || 'there');
  const speciesHtml = speciesListHtml(ctx.requestedSpecies);
  const referenceStructures = (isCnc ? ctx.cncStructures : ctx.timberStructures) ?? [];
  const referenceStructuresHtml = referenceStructures.length
    ? ` It covers <strong>${escapeHtml(referenceStructures.join(', '))}</strong>.`
    : '';
  const referenceLabelHtml = (ctx.releaseLabel || '').trim()
    ? ` (<strong>${escapeHtml(ctx.releaseLabel!.trim())}</strong>)`
    : '';
  const referenceFallback = `<p style="color: #4F423A; line-height: 1.65; margin: 0 0 16px; font-size: 14px;">
        Hi ${first},<br/><br/>
        We have refined the ${isCnc ? 'model' : 'timber list'} for the package you already quoted and we approved${referenceLabelHtml}.${referenceStructuresHtml}<br/><br/>
        <strong>This is a reference release — please do not requote.</strong> Use this version to cut the package; your approved pricing stands.
        ${isCnc ? 'Download the updated model files from the vendor portal.' : 'The updated list is attached and available in the vendor portal.'}
      </p>`;

  const newRequestFallback = isCnc
    ? `<p style="color: #4F423A; line-height: 1.65; margin: 0 0 16px; font-size: 14px;">
        Hi ${first},<br/><br/>
        A new CNC quote request is available for bidding. Please download the released model files from the vendor portal and submit your quote when you are ready.<br/><br/>
        Both the model files and more detailed project information are available in the project portal.
      </p>`
    : `<p style="color: #4F423A; line-height: 1.65; margin: 0 0 16px; font-size: 14px;">
        Hi ${first},<br/><br/>
        A new timber RFQ package is available for bidding. Please provide pricing for the following species: ${speciesHtml}.
        Please find the CSV attached to this email. Both the CSV and more detailed project information are available in the project portal.
      </p>`;
  const scopeChangeFallback = isCnc
    ? `<p style="color: #4F423A; line-height: 1.65; margin: 0 0 16px; font-size: 14px;">
        Hi ${first},<br/><br/>
        We have updated which structures we need you to quote on this project. This is not a new file — the model files you already received are unchanged.<br/><br/>
        The updated structure list is below and in the vendor portal.
      </p>`
    : `<p style="color: #4F423A; line-height: 1.65; margin: 0 0 16px; font-size: 14px;">
        Hi ${first},<br/><br/>
        We have updated which structures we need you to quote on this project. This is not a new timber list version — only your requested scope changed.
        The attached CSV reflects your updated scope, and the full details are in the vendor portal.
      </p>`;
  const round = ctx.requestRound;
  const requestRoundGreeting = round ? requestRoundGreetingHtml(first, round) : '';
  const fallbackBody = round
    ? requestRoundGreeting
    : isReference
      ? referenceFallback
      : isScopeChange
        ? scopeChangeFallback
        : newRequestFallback;

  const staffNote = (ctx.customMessage || '').trim();
  const body = round
    ? boldSpeciesInHtml(requestRoundGreeting, ctx.requestedSpecies)
      + (staffNote ? messageHtml(personalizeRfqPublishEmailMessage(staffNote, ctx), '') : '')
    : boldSpeciesInHtml(
        messageHtml(personalizeRfqPublishEmailMessage(staffNote, ctx), fallbackBody),
        ctx.requestedSpecies,
      );
  const heading = round
    ? isGenericRfqRequestLabel(round.label) ? 'Timber quote request' : `Quote request — ${round.label.trim()}`
    : isReference
      ? isCnc
        ? 'Updated model for production — no requote'
        : 'Updated timber list for production — no requote'
      : isScopeChange
        ? 'Updated scope — same file as before'
        : isCnc
          ? 'New CNC quote request'
          : 'New timber RFQ package';
  const ctaLabel = 'Open Vendor Portal';

  const bodyRows = `
          <tr>
            <td style="background: #2C3E2F; padding: 28px 24px 22px; border-radius: 10px 10px 0 0; text-align: center;">
              <img src="${escapeHtml(logoUrl)}" alt="${companyName}" width="160" style="display:block;margin:0 auto 10px;border:0;width:160px;height:auto;max-width:160px;" />
              <p style="color: #C8D4CA; margin: 0 0 4px; font-size: 12px; font-style: italic; letter-spacing: 0.01em; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">Partnering with</p>
              <p style="color: #FFFFFF; margin: 0; font-size: 13px; font-weight: 600; letter-spacing: 0.02em; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">${vendorHeader}</p>
            </td>
          </tr>
          <tr>
            <td style="background: #FFFFFF; padding: 28px 24px; border: 1px solid #E2E0DB; border-top: none; border-radius: 0 0 10px 10px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
              <p style="color: #1A1A1A; font-size: 17px; font-weight: 650; margin: 0 0 16px;">${heading}</p>
              ${isReference ? buildReferenceReleaseBannerHtml(ctx) : buildScopeChangeBannerHtml(ctx)}
              ${body}
              ${buildProjectDetailsBlock(ctx)}
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin: 0 0 4px;">
                <tr>
                  <td style="border-radius: 6px; background: #2C3E2F;">
                    <a href="${portalUrl}" style="display: inline-block; padding: 14px 32px; background: #2C3E2F; color: #FFFFFF; text-decoration: none; border-radius: 6px; font-weight: 600; font-size: 14px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">${ctaLabel}</a>
                  </td>
                </tr>
              </table>
              ${buildSenderSignature(ctx)}
            </td>
          </tr>`;

  return wrapRfqEmailDocument(heading, bodyRows);
}
